import { dataMapperPublicApi } from 'packages/tackle-api';
import {
  Cloud,
  Domain,
  Operation,
} from 'pages/Settings/Account/components/FieldMapper/utils/constants';
import { reportError } from 'utils/monitor';
import { z } from 'zod';

const NullableString = z.string().nullable();
const ASSISTANCE_REQUEST_CODES = [
  'GeneralOrOther',
  'CustomerTechnicalArchitecture',
  'ProofOfConceptOrDemo',
  'QuotesOrLicensing',
  'PostSalesCustomerSuccess',
  'WorkloadSpecificValueProposition',
  'NoHelpNeededFromMicrosoft',
] as const;

/** Lookup initialization data */
const CustomerLookupInput = z
  .object({
    address: z.object({
      addressLine1: NullableString,
      city: NullableString,
      country: NullableString,
      postalCode: NullableString,
      region: NullableString,
    }),
    companyName: NullableString,
  })
  .partial();

const SolutionLookupIds = z.array(z.string()).nullable();

/** initialValues data */
const MemberInput = z
  .object({
    firstName: NullableString,
    lastName: NullableString,
    email: NullableString,
    phoneNumber: NullableString,
  })
  .partial();

const DetailsInput = z
  .object({
    closingDateTime: NullableString,
    customerNeed: NullableString,
    dealValue: z
      .union([z.number(), z.string()])
      .nullable()
      .transform((value) => {
        // NOTE: [sw] this is a temp solution to handle default values that are strings
        // and need to be converted to numbers. We are handling this here in the zod schema for now
        if (!value) return null;

        if (typeof value !== 'number') {
          const parsedNumber = Number(value);
          if (isNaN(parsedNumber)) return null;

          return parsedNumber;
        }

        return value;
      }),
    notes: NullableString,
    requirements: z
      .object({
        solutionArea: NullableString,
        solutionPlay: NullableString,
      })
      .nullable(),
  })
  .partial()
  .passthrough();

const InviteContext = z
  .object({
    assistanceRequestCode: z.enum(ASSISTANCE_REQUEST_CODES).nullable(),
    notes: z.string().nullable(),
  })
  .partial()
  .passthrough();

const ReferralInput = z
  .object({
    customerProfile: z.object({
      team: z.array(MemberInput),
    }),
    details: DetailsInput,
    name: NullableString,
    inviteContexts: z.array(InviteContext),
    team: z.array(MemberInput).nullable(),
    campaignId: NullableString,
    mpnId: NullableString,
  })
  .partial()
  .passthrough();

const MsftMappedFields = z
  .object({
    customerLookupInput: CustomerLookupInput.nullable(),
    solutionLookupIds: SolutionLookupIds,
    referralInput: ReferralInput,
  })
  .partial();

export type MsftMappedFieldsType = z.infer<typeof MsftMappedFields>;

export const getMsftReferral_mappedFields_Response = z
  .object({
    '$tkl-metadata': z.string(),
    errors: z.record(z.string()).nullable(),
    message: NullableString,
    mappedFields: MsftMappedFields,
    isValid: z.boolean(),
  })
  .partial()
  .passthrough();

/**
 * Fetch mapped fields for MSFT co-sell from data-mapper-api a given crmId (salesforce record id) and mappingId.
 */
export const getMsftReferralMappedFields = async <T extends Operation>({
  crmId,
  operation,
  mappingId,
}: {
  crmId: string;
  operation: T;
  mappingId: string;
}) => {
  const queryParams = new URLSearchParams({
    crmId,
    mappingId,
    cloud: Cloud.MSFT,
    domain: Domain.CO_SELL,
    operation,
  });

  try {
    const response = await dataMapperPublicApi
      .get(`mapped-fields?${queryParams?.toString()}`)
      .json();

    return getMsftReferral_mappedFields_Response.parse(response);
  } catch (error) {
    reportError(error);
    throw error;
  }
};
